import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
const AnimatedOrb = ({ isActive }) => {
    const canvasRef = useRef(null);
    const animationRef = useRef(null);
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas)
            return;
        const ctx = canvas === null || canvas === void 0 ? void 0 : canvas.getContext('2d');
        const size = 500;
        canvas.width = size;
        canvas.height = size;
        let time = 0;
        // Noise function for organic movement
        const noise = (x, y, z) => {
            return Math.sin(x * 1.5 + z) * Math.cos(y * 1.5 + z) * 0.5 + 0.5;
        };
        const drawOrb = () => {
            // Clear canvas completely for transparency
            ctx.clearRect(0, 0, size, size);
            const centerX = size / 2;
            const centerY = size / 2;
            const baseRadius = size / 3.2;
            if (isActive) {
                // Active state - speaking effect with controlled scaling
                const speakPulse1 = Math.sin(time * 8) * 0.15;
                const speakPulse2 = Math.sin(time * 12 + 1) * 0.1;
                const speakPulse3 = Math.sin(time * 6 + 0.5) * 0.12;
                const combinedPulse = 1 + speakPulse1 + speakPulse2 + speakPulse3;
                const radius = baseRadius * combinedPulse;
                // Base luminous sphere with high contrast vibrant colors
                const baseGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
                baseGradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
                baseGradient.addColorStop(0.2, 'rgba(0, 200, 255, 0.95)');
                baseGradient.addColorStop(0.5, 'rgba(0, 150, 255, 0.7)');
                baseGradient.addColorStop(1, 'rgba(0, 100, 255, 0)');
                ctx.globalCompositeOperation = 'screen';
                ctx.beginPath();
                ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
                ctx.fillStyle = baseGradient;
                ctx.fill();
                // Fluid cloud-like motion inside
                const numClouds = 6;
                for (let i = 0; i < numClouds; i++) {
                    const t = time * (0.3 + i * 0.1);
                    const angle = t + i * Math.PI * 0.33;
                    const noiseX = noise(Math.cos(angle), Math.sin(angle), t * 0.5);
                    const noiseY = noise(Math.sin(angle), Math.cos(angle), t * 0.5 + 10);
                    const offsetX = (noiseX - 0.5) * radius * 0.8;
                    const offsetY = (noiseY - 0.5) * radius * 0.8;
                    const cloudX = centerX + offsetX;
                    const cloudY = centerY + offsetY;
                    const cloudSize = radius * (0.5 + Math.sin(t * 1.5 + i) * 0.2);
                    const intensity = (Math.sin(t * 2 + i) + 1) / 2;
                    const cloudGradient = ctx.createRadialGradient(cloudX, cloudY, 0, cloudX, cloudY, cloudSize);
                    cloudGradient.addColorStop(0, `rgba(255, 255, 255, ${0.9 * intensity})`);
                    cloudGradient.addColorStop(0.3, `rgba(100, 220, 255, ${0.7 * intensity})`);
                    cloudGradient.addColorStop(0.6, `rgba(0, 180, 255, ${0.4 * intensity})`);
                    cloudGradient.addColorStop(1, 'rgba(0, 150, 255, 0)');
                    ctx.beginPath();
                    ctx.arc(cloudX, cloudY, cloudSize, 0, Math.PI * 2);
                    ctx.fillStyle = cloudGradient;
                    ctx.fill();
                }
                // Aurora-like swirls
                for (let i = 0; i < 3; i++) {
                    const swirlAngle = time * (0.4 + i * 0.15) + i * Math.PI * 0.66;
                    const swirlDist = Math.sin(time * 1.2 + i) * radius * 0.4;
                    const swirlX = centerX + Math.cos(swirlAngle) * swirlDist;
                    const swirlY = centerY + Math.sin(swirlAngle) * swirlDist;
                    const swirlGradient = ctx.createRadialGradient(swirlX, swirlY, 0, swirlX, swirlY, radius * 0.6);
                    swirlGradient.addColorStop(0, 'rgba(0, 220, 255, 0.6)');
                    swirlGradient.addColorStop(0.5, 'rgba(0, 180, 255, 0.3)');
                    swirlGradient.addColorStop(1, 'rgba(0, 140, 255, 0)');
                    ctx.beginPath();
                    ctx.arc(swirlX, swirlY, radius * 0.6, 0, Math.PI * 2);
                    ctx.fillStyle = swirlGradient;
                    ctx.fill();
                }
                // Outer glow/bloom - bright and intense
                const glowGradient = ctx.createRadialGradient(centerX, centerY, radius * 0.8, centerX, centerY, radius * 1.5);
                glowGradient.addColorStop(0, 'rgba(0, 200, 255, 0.4)');
                glowGradient.addColorStop(0.5, 'rgba(0, 150, 255, 0.2)');
                glowGradient.addColorStop(1, 'rgba(0, 100, 255, 0)');
                ctx.beginPath();
                ctx.arc(centerX, centerY, radius * 1.4, 0, Math.PI * 2);
                ctx.fillStyle = glowGradient;
                ctx.fill();
            }
            else {
                // Inactive/listening state - calm breathing with brighter colors
                const radius = baseRadius;
                // Bright base sphere
                const baseGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
                baseGradient.addColorStop(0, 'rgba(200, 240, 255, 0.95)');
                baseGradient.addColorStop(0.4, 'rgba(0, 200, 255, 0.8)');
                baseGradient.addColorStop(0.7, 'rgba(0, 160, 255, 0.5)');
                baseGradient.addColorStop(1, 'rgba(0, 120, 255, 0)');
                ctx.globalCompositeOperation = 'screen';
                ctx.beginPath();
                ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
                ctx.fillStyle = baseGradient;
                ctx.fill();
                // Gentle internal clouds
                const numClouds = 3;
                for (let i = 0; i < numClouds; i++) {
                    const t = time * 0.5 + i * Math.PI * 0.66;
                    const angle = t;
                    const noiseX = noise(Math.cos(angle) * 0.5, Math.sin(angle) * 0.5, t * 0.3);
                    const noiseY = noise(Math.sin(angle) * 0.5, Math.cos(angle) * 0.5, t * 0.3 + 5);
                    const offsetX = (noiseX - 0.5) * radius * 0.5;
                    const offsetY = (noiseY - 0.5) * radius * 0.5;
                    const cloudX = centerX + offsetX;
                    const cloudY = centerY + offsetY;
                    const cloudSize = radius * 0.6;
                    const cloudGradient = ctx.createRadialGradient(cloudX, cloudY, 0, cloudX, cloudY, cloudSize);
                    cloudGradient.addColorStop(0, 'rgba(255, 255, 255, 0.6)');
                    cloudGradient.addColorStop(0.4, 'rgba(150, 230, 255, 0.4)');
                    cloudGradient.addColorStop(0.7, 'rgba(0, 200, 255, 0.25)');
                    cloudGradient.addColorStop(1, 'rgba(0, 150, 255, 0)');
                    ctx.beginPath();
                    ctx.arc(cloudX, cloudY, cloudSize, 0, Math.PI * 2);
                    ctx.fillStyle = cloudGradient;
                    ctx.fill();
                }
                // Breathing shimmer - brighter
                const breathe = (Math.sin(time * 1.5) + 1) / 2;
                const shimmerGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius * 0.7);
                shimmerGradient.addColorStop(0, `rgba(255, 255, 255, ${0.5 * breathe})`);
                shimmerGradient.addColorStop(0.5, `rgba(100, 220, 255, ${0.3 * breathe})`);
                shimmerGradient.addColorStop(1, 'rgba(0, 180, 255, 0)');
                ctx.beginPath();
                ctx.arc(centerX, centerY, radius * 0.7, 0, Math.PI * 2);
                ctx.fillStyle = shimmerGradient;
                ctx.fill();
            }
            ctx.globalCompositeOperation = 'source-over';
            time += 0.02;
            animationRef.current = requestAnimationFrame(drawOrb);
        };
        drawOrb();
        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [isActive]);
    return (_jsx("div", { className: "flex min-h-screen flex-col items-center justify-center gap-8 bg-transparent", children: _jsx("div", { className: "relative", children: _jsx("canvas", { ref: canvasRef, className: "h-[500px] w-[500px]", style: { filter: 'blur(0.5px)' } }) }) }));
};
export default AnimatedOrb;
